import React from 'react';
import PropTypes from 'prop-types';
import { FormattedNumber } from 'react-intl';
import {
  Button, Message, Modal, Segment,
} from 'semantic-ui-react';

import {
  ACTIVATE_CONFIRM_LABEL,
  ACTIVATE_COVERAGE_NOTICE,
  ACTIVATE_DIALOG_HEADER,
  ACTIVATE_IRREVERSIBLE_NOTICE,
  DIALOG_CANCEL_LABEL,
  NO_AGREED_PRICE_MESSAGE,
  VALUE_PLACEHOLDER,
} from '../../constants/onboardingQueue';
import { coverageStartDate, hasAgreedPrice, priceProps } from '../../utilities/onboardingQueue';

const fieldValue = value => (value == null || value === '' ? VALUE_PLACEHOLDER : value);

const ActivateDialog = ({
  record, loading, errorMessage, onClose, onConfirm,
}) => {
  const hasPrice = hasAgreedPrice(record.subscriptionPrice);

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  return (
    <Modal
      open
      size='small'
      onClose={handleClose}
      closeOnEscape={!loading}
      closeOnDimmerClick={false}
      data-testid='queue-activate-dialog'
    >
      <Modal.Header>{ACTIVATE_DIALOG_HEADER}</Modal.Header>
      <Modal.Content>
        <Segment data-testid='queue-activate-summary'>
          <div>
            <b>Customer:</b>
            {' '}
            <span data-testid='queue-activate-dealer'>{fieldValue(record.dealerName)}</span>
          </div>
          <div>
            <b>Customer ID:</b>
            {' '}
            <span data-testid='queue-activate-customer-id'>{fieldValue(record.customerId)}</span>
          </div>
          <div>
            <b>Product:</b>
            {' '}
            <span data-testid='queue-activate-product'>{fieldValue(record.productType)}</span>
          </div>
          <div>
            <b>Price:</b>
            {' '}
            {hasPrice && (
              <span data-testid='queue-activate-price'>
                {React.createElement(FormattedNumber, priceProps(record.subscriptionPrice))}
              </span>
            )}
            {!hasPrice && (
              <span data-testid='queue-activate-no-price'>{NO_AGREED_PRICE_MESSAGE}</span>
            )}
          </div>
          <div>
            <b>{ACTIVATE_COVERAGE_NOTICE}</b>
            {' '}
            <span data-testid='queue-activate-coverage-date'>{coverageStartDate()}</span>
          </div>
        </Segment>

        <Message warning data-testid='queue-activate-irreversible'>
          <p>{ACTIVATE_IRREVERSIBLE_NOTICE}</p>
        </Message>

        {errorMessage && (
          <Message negative data-testid='queue-activate-error'>
            <p>{errorMessage}</p>
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose} disabled={loading} data-testid='queue-activate-cancel'>
          {DIALOG_CANCEL_LABEL}
        </Button>
        <Button
          primary
          loading={loading}
          disabled={loading || !hasPrice}
          data-testid='queue-activate-confirm'
          onClick={() => onConfirm()}
        >
          {ACTIVATE_CONFIRM_LABEL}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

ActivateDialog.propTypes = {
  record: PropTypes.shape({
    dealerName: PropTypes.string,
    customerId: PropTypes.string,
    productType: PropTypes.string,
    subscriptionPrice: PropTypes.number,
  }).isRequired,
  loading: PropTypes.bool,
  errorMessage: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

ActivateDialog.defaultProps = {
  loading: false,
  errorMessage: null,
};

export default ActivateDialog;
